import { useEffect, useRef, useState } from "react";
import { SHARE_DAYS, type CompanyBranding, type ProtocolShareInfo, type ProtocolSummary } from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { Icon, Message, Sheet } from "./ui";

/*
 * Firmenkopf der Protokolle (Name, Zusatzzeile, Logo) und Versand abgeschlossener Protokolle an Kunden per Link.
 */

let cache: CompanyBranding | null = null;
const listeners = new Set<(b: CompanyBranding) => void>();

function publish(b: CompanyBranding) {
  cache = b;
  for (const l of listeners) l(b);
}

/** Firmenkopf laden; Änderungen erscheinen sofort überall, wo er angezeigt wird. */
export function useBranding(): CompanyBranding | null {
  const [b, setB] = useState(cache);
  useEffect(() => {
    listeners.add(setB);
    api
      .get<CompanyBranding>("/api/company/branding")
      .then(publish)
      .catch(() => undefined);
    return () => {
      listeners.delete(setB);
    };
  }, []);
  return b;
}

const logoUrl = (b: CompanyBranding) => `/api/company/logo?v=${b.logoVersion ?? ""}`;

/** Kopf wie oben auf dem PDF: Logo und Firmenname. */
export function BrandStrip({ branding, onEdit }: { branding: CompanyBranding | null; onEdit?: () => void }) {
  if (!branding) return null;
  const empty = !branding.hasLogo && !branding.customName && !branding.line;
  return (
    <div className="brand-strip">
      {branding.hasLogo && <img src={logoUrl(branding)} alt="" className="brand-logo" />}
      <span className="brand-text">
        <b>{branding.name}</b>
        {branding.line && <small>{branding.line}</small>}
        {!branding.line && empty && branding.canEdit && <small>So steht Ihre Firma auf jedem Protokoll. Mit Logo wirkt es gleich professioneller.</small>}
      </span>
      {onEdit && branding.canEdit && (
        <button className="app-btn small ghost" onClick={onEdit}>
          <Icon name="pen" /> {empty ? "Logo hinzufügen" : "Ändern"}
        </button>
      )}
    </div>
  );
}

export function BrandingSheet({ branding, onClose }: { branding: CompanyBranding; onClose: () => void }) {
  const [name, setName] = useState(branding.customName);
  const [line, setLine] = useState(branding.line);
  const [current, setCurrent] = useState(branding);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  async function run(job: () => Promise<CompanyBranding>, close = false) {
    setBusy(true);
    setError("");
    try {
      const b = await job();
      publish(b);
      setCurrent(b);
      if (close) onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function pick(file: File | undefined) {
    if (!file) return;
    const form = new FormData();
    form.append("logo", file, file.name);
    void run(() => upload<CompanyBranding>("/api/company/logo", form));
  }

  return (
    <Sheet title="Firmenkopf" onClose={onClose}>
      <div className="app-form">
        <p>So erscheint Ihre Firma oben auf jedem neuen Protokoll, im PDF und auf der Download-Seite für Ihre Kunden. Bereits abgeschlossene PDFs bleiben unverändert.</p>
        <div className="brand-logo-edit">
          {current.hasLogo ? <img src={logoUrl(current)} alt="Ihr Logo" className="brand-logo big" /> : <span className="brand-logo empty">Kein Logo</span>}
          <div>
            <button className="app-btn small" disabled={busy} onClick={() => fileRef.current?.click()}>
              {current.hasLogo ? "Anderes Logo wählen" : "Logo hochladen"}
            </button>
            {current.hasLogo && (
              <button className="btn-text" disabled={busy} onClick={() => void run(() => api.del<CompanyBranding>("/api/company/logo"))}>
                Logo entfernen
              </button>
            )}
            <small>PNG oder JPG, bis 1 MB. Am besten ein Querformat mit transparentem Hintergrund.</small>
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden onChange={(e) => pick(e.target.files?.[0])} />
        </div>
        <label>
          Name im Protokoll
          <input value={name} maxLength={120} placeholder={current.companyName} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Zusatzzeile (freiwillig)
          <input value={line} maxLength={200} placeholder="z. B. Hauptstraße 1 · 09481 Elterlein · Tel. 037349 12345" onChange={(e) => setLine(e.target.value)} />
        </label>
        <Message>{error}</Message>
        <button className="app-btn" disabled={busy} onClick={() => void run(() => api.put<CompanyBranding>("/api/company/branding", { name, line }), true)}>
          Speichern
        </button>
      </div>
    </Sheet>
  );
}

function day(iso: string) {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Abgeschlossene Protokolle per E-Mail-Link an einen Kunden schicken; darunter die bisher verschickten Links. */
export function ShareSheet(props: { projectId: string; protocols: ProtocolSummary[]; preselect?: string; customerHint?: string; onClose: () => void }) {
  const ready = props.protocols.filter((p) => p.status === "completed" && p.fileId);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(props.preselect ? [props.preselect] : ready.map((p) => p.id)));
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [days, setDays] = useState<number>(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const [shares, setShares] = useState<ProtocolShareInfo[] | null>(null);

  useEffect(() => {
    api
      .get<ProtocolShareInfo[]>(`/api/projects/${props.projectId}/protocol-shares`)
      .then(setShares)
      .catch(() => setShares([]));
  }, [props.projectId]);

  function toggle(id: string) {
    setChosen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function send() {
    setBusy(true);
    setError("");
    setSent("");
    try {
      const s = await api.post<ProtocolShareInfo>(`/api/projects/${props.projectId}/protocol-shares`, {
        protocolIds: [...chosen],
        email,
        message: message.trim() || undefined,
        days,
      });
      setShares((l) => [s, ...(l ?? [])]);
      setSent(`Gesendet an ${s.email}. Der Link ist bis zum ${day(s.expiresAt)} gültig.`);
      setEmail("");
      setMessage("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(s: ProtocolShareInfo) {
    if (!window.confirm(`Link an ${s.email} zurückziehen? Danach kann der Kunde darüber nichts mehr herunterladen.`)) return;
    try {
      await api.del(`/api/protocol-shares/${s.id}`);
      setShares((l) => (l ?? []).map((x) => (x.id === s.id ? { ...x, revoked: true } : x)));
    } catch (err) {
      setError(errorText(err));
    }
  }

  const now = Date.now();
  return (
    <Sheet title="An Kunden senden" onClose={props.onClose}>
      <div className="app-form">
        {ready.length === 0 ? (
          <p>Verschicken lassen sich abgeschlossene Protokolle. Schließen Sie zuerst ein Protokoll ab, dann geht es hier weiter.</p>
        ) : (
          <>
            <p>Ihr Kunde bekommt eine E-Mail mit einem Link und lädt die PDFs darüber herunter, ganz ohne Konto. Die Mail kommt von HandVero im Namen Ihrer Firma; Antworten gehen direkt an Sie.</p>
            <fieldset className="share-pick">
              <legend>Protokolle</legend>
              {ready.map((p) => (
                <label key={p.id} className="share-item">
                  <input type="checkbox" checked={chosen.has(p.id)} onChange={() => toggle(p.id)} />
                  <span>
                    <b>{p.title}</b>
                    <small>Abgeschlossen am {day(p.completedAt ?? p.updatedAt)}</small>
                  </span>
                </label>
              ))}
            </fieldset>
            <label>
              E-Mail-Adresse des Kunden
              <input type="email" inputMode="email" autoComplete="off" value={email} placeholder={props.customerHint ? `z. B. Ansprechpartner bei ${props.customerHint}` : "name@kunde.de"} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label>
              Nachricht (freiwillig)
              <textarea rows={3} maxLength={1000} value={message} placeholder="z. B. Anbei die Prüfprotokolle vom Montag." onChange={(e) => setMessage(e.target.value)} />
            </label>
            <label>
              Link gültig für
              <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                {SHARE_DAYS.map((d) => (
                  <option key={d} value={d}>
                    {d} Tage
                  </option>
                ))}
              </select>
            </label>
            <Message>{error}</Message>
            <Message kind="ok">{sent}</Message>
            <button className="app-btn" disabled={busy || !chosen.size || !email.trim()} onClick={() => void send()}>
              <Icon name="send" /> {busy ? "Wird gesendet …" : chosen.size === 1 ? "Protokoll senden" : `${chosen.size} Protokolle senden`}
            </button>
          </>
        )}

        {!!shares?.length && (
          <section className="share-list">
            <h3 className="prot-h">Bisher verschickt</h3>
            <ul>
              {shares.map((s) => {
                const expired = new Date(s.expiresAt).getTime() < now;
                const state = s.revoked ? "Zurückgezogen" : expired ? "Abgelaufen" : `Gültig bis ${day(s.expiresAt)}`;
                return (
                  <li key={s.id} className={s.revoked || expired ? "off" : ""}>
                    <span>
                      <b>{s.email}</b>
                      <small>
                        {day(s.createdAt)}
                        {s.createdBy && ` · ${s.createdBy}`} · {s.protocols.length === 1 ? s.protocols[0]!.title : `${s.protocols.length} Protokolle`}
                      </small>
                      <small>
                        {state} · {s.downloads ? `${s.downloads}× heruntergeladen` : s.lastOpenedAt ? "Geöffnet" : "Noch nicht geöffnet"}
                      </small>
                    </span>
                    {!s.revoked && !expired && (
                      <button className="btn-text" onClick={() => void revoke(s)}>
                        Zurückziehen
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </Sheet>
  );
}
